import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';

export default function Cart() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingItems, setUpdatingItems] = useState({});

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchCart = async () => {
      try {
        const { data } = await api.get('/cart');
        setCart(data.data);
      } catch (err) {
        console.error('Error fetching cart:', err);
        setError('Failed to load cart');
      } finally {
        setLoading(false);
      }
    };

    fetchCart();
  }, [user, navigate]);

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    setUpdatingItems(prev => ({ ...prev, [itemId]: true }));
    try {
      await api.put(`/cart/${itemId}`, { quantity: newQuantity });
      const { data } = await api.get('/cart');
      setCart(data.data);
      toast.success('Cart updated!');
    } catch (err) {
      console.error('Error updating cart:', err);
      toast.error(err.response?.data?.message || 'Failed to update cart');
    } finally {
      setUpdatingItems(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleRemoveItem = async (itemId) => {
    setUpdatingItems(prev => ({ ...prev, [itemId]: true }));
    try {
      await api.delete(`/cart/${itemId}`);
      const { data } = await api.get('/cart');
      setCart(data.data);
      toast.success('Item removed from cart!');
    } catch (err) {
      console.error('Error removing item:', err);
      toast.error(err.response?.data?.message || 'Failed to remove item');
    } finally {
      setUpdatingItems(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleClearCart = async () => {
    try {
      await api.delete('/cart');
      setCart({
        items: [],
        totalItems: 0,
        subtotal: 0
      });
      toast.success('Cart cleared!');
    } catch (err) {
      console.error('Error clearing cart:', err);
      toast.error(err.response?.data?.message || 'Failed to clear cart');
    }
  };

  const handleCheckout = () => {
    if (!cart?.items?.length) {
      toast.error('Your cart is empty');
      return;
    }
    navigate('/checkout');
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-pulse text-pink-600 text-lg">Loading your cart...</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center text-red-500">
      {error}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Your Shopping Cart</h1>
      
      {cart?.items?.length ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map(item => (
              <div key={item._id} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4">
                {/* Product Image */}
                <div className="w-full sm:w-32 h-32 flex-shrink-0">
                  <img
                    src={item.product?.images?.[0] || 'https://via.placeholder.com/150'}
                    alt={item.product?.name}
                    className="w-full h-full object-cover rounded-md"
                  />
                </div>
                
                {/* Product Info */}
                <div className="flex-grow">
                  <div className="flex justify-between">
                    <h3 className="font-medium text-gray-900">{item.product?.name}</h3>
                    <button 
                      onClick={() => handleRemoveItem(item._id)}
                      disabled={updatingItems[item._id]}
                      className="text-gray-400 hover:text-red-500 transition"
                    >
                      {updatingItems[item._id] ? '...' : '✕'}
                    </button>
                  </div>
                  
                  <p className="text-sm text-gray-500 mb-2">
                    {item.product?.variants?.find(v => v._id === item.variant)?.color?.name || 
                     item.product?.variants?.find(v => v._id === item.variant)?.size || 
                     'Standard'}
                  </p>
                  
                  <p className="text-pink-600 font-medium mb-4">₹{item.price}</p>
                  
                  {/* Quantity Controls */}
                  <div className="flex items-center">
                    <button 
                      onClick={() => handleQuantityChange(item._id, item.quantity - 1)}
                      disabled={updatingItems[item._id] || item.quantity <= 1}
                      className="px-3 py-1 border border-gray-300 rounded-l-md disabled:opacity-50"
                    >
                      -
                    </button>
                    <span className="px-4 py-1 border-t border-b border-gray-300">
                      {updatingItems[item._id] ? '...' : item.quantity}
                    </span>
                    <button 
                      onClick={() => handleQuantityChange(item._id, item.quantity + 1)}
                      disabled={updatingItems[item._id]}
                      className="px-3 py-1 border border-gray-300 rounded-r-md disabled:opacity-50"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Clear Cart Button */}
            <button
              onClick={handleClearCart}
              className="mt-6 text-sm text-gray-500 hover:text-red-500 transition flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Clear Cart
            </button>
          </div>
          
          {/* Order Summary */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 h-fit sticky top-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Order Summary</h2>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal ({cart.totalItems} items)</span>
                <span className="font-medium">₹{cart.subtotal?.toFixed(2) || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Shipping</span>
                <span className="font-medium">FREE</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-3">
                <span className="text-gray-900 font-semibold">Total</span>
                <span className="text-pink-600 font-bold">₹{cart.subtotal?.toFixed(2) || 0}</span>
              </div>
            </div>
            
            <button
              onClick={handleCheckout}
              className="w-full bg-pink-600 hover:bg-pink-700 text-white py-3 rounded-md font-medium transition"
            >
              Proceed to Checkout
            </button>
            
            <p className="text-xs text-gray-500 mt-4 text-center">
              Secure checkout with SSL encryption
            </p>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Your cart is empty</h3>
          <p className="mt-1 text-gray-500">Start shopping to add items to your cart</p>
          <button
            onClick={() => navigate('/stores')}
            className="mt-6 bg-pink-600 hover:bg-pink-700 text-white px-6 py-2 rounded-md transition"
          >
            Continue Shopping
          </button>
        </div>
      )}
    </div>
  );
}